import { useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'
import area from '@turf/area'
import { featureCollection } from '@turf/helpers'
import { FeatureCollection, Point, Polygon } from 'geojson'
import { Permit } from '@core/entities/permit/Permit'

type PermitMapFeatures = {
  zones: FeatureCollection<Polygon>
  markers: FeatureCollection<Point>
}

/**
 * Builds the GeoJSON FeatureCollections that render permits on a map — zones
 * (drawn polygons) and markers (W3W points). Each feature is tagged with
 * `permitId` so consumers can cross-reference the clicked/hovered feature.
 * Zones are sorted largest-first so smaller overlapping zones stay clickable.
 */
export const usePermitMapFeatures = (
  permits: Permit[] | undefined,
): PermitMapFeatures => {
  const zones = useMemo(() => {
    const featurePolygons = permits
      ?.filter((permit) => !!permit.getGeolocationAreas())
      ?.flatMap((permit) => {
        const collection = permit.getGeolocationAreas()
        return collection.features.map((featurePolygon) => {
          featurePolygon.properties.id = uuidv4()
          featurePolygon.properties.permitId = permit.id
          featurePolygon.properties.selected = false
          return featurePolygon
        })
      })
      ?.sort((a, b) => area(b) - area(a))

    return featureCollection(featurePolygons ?? [])
  }, [permits])

  const markers = useMemo(() => {
    const featurePoints = permits
      ?.filter((permit) => !!permit.getW3wLocation())
      ?.map((permit) => {
        const featurePoint = permit.getW3wLocation()
        featurePoint.properties.id = uuidv4()
        featurePoint.properties.permitId = permit.id
        featurePoint.properties.selected = false
        return featurePoint
      })

    return featureCollection(featurePoints ?? [])
  }, [permits])

  return { zones, markers }
}
